// ============================================================
// Contrack AI — Onboarding content, icons, logo, helpers
// Faithful TSX port of the prototype's data.jsx.
// All UI copy in Norwegian. No emojis in UI.
// ============================================================
import type { CSSProperties, ReactNode, ReactElement } from "react";
import type { ObCopy } from "./i18n";

// ---- Line icons (consistent 24px grid, stroke 1.6, round caps) ----
const stroke = {
  fill: "none",
  stroke: "currentColor",
  strokeWidth: 1.6,
  strokeLinecap: "round" as const,
  strokeLinejoin: "round" as const,
};

export interface IconProps {
  size?: number;
  style?: CSSProperties;
}

function Icon({ d, children, size = 24, style }: IconProps & { d?: string; children?: ReactNode }) {
  return (
    <svg width={size} height={size} viewBox="0 0 24 24" style={style} aria-hidden="true">
      {d ? <path d={d} {...stroke} /> : <g {...stroke}>{children}</g>}
    </svg>
  );
}

// Industry icons
export const IconConstruction = (p: IconProps) => (
  <Icon {...p}>
    <path d="M3 21h18" />
    <path d="M5 21V8.5L12 4l7 4.5V21" />
    <path d="M9.5 21v-5h5v5" />
    <path d="M9.5 11.5h0M14.5 11.5h0" />
  </Icon>
);
export const IconRealEstate = (p: IconProps) => (
  <Icon {...p}>
    <path d="M4 21V6l6-3 6 3v15" />
    <path d="M16 21V9l4 2v10" />
    <path d="M3 21h18" />
    <path d="M7.5 8.5h0M7.5 12.5h0M7.5 16.5h0M12.5 8.5h0M12.5 12.5h0M12.5 16.5h0" />
  </Icon>
);
export const IconRestaurant = (p: IconProps) => (
  <Icon {...p}>
    <path d="M6 3v7a2 2 0 0 0 4 0V3" />
    <path d="M8 10v11" />
    <path d="M16 3c-1.5 0-2.5 1.8-2.5 4.5S14.5 12 16 12v9" />
  </Icon>
);
export const IconLogistics = (p: IconProps) => (
  <Icon {...p}>
    <path d="M2.5 7.5h11v9h-11z" />
    <path d="M13.5 10.5h4l3 3v3h-7z" />
    <circle cx="7" cy="17.5" r="1.7" />
    <circle cx="17" cy="17.5" r="1.7" />
  </Icon>
);
export const IconIndustry = (p: IconProps) => (
  <Icon {...p}>
    <path d="M3 21V11l5 3.5V11l5 3.5V8l6 4v9z" />
    <path d="M3 21h18" />
  </Icon>
);
export const IconOther = (p: IconProps) => (
  <Icon {...p}>
    <circle cx="12" cy="12" r="9" />
    <path d="M9.5 9.5a2.5 2.5 0 1 1 3.2 2.4c-.7.25-1.2.9-1.2 1.6v.4" />
    <path d="M11.5 17h0" />
  </Icon>
);

// Goal icons
export const IconShield = (p: IconProps) => (
  <Icon {...p}>
    <path d="M12 3l7 2.5v5.5c0 4.5-3 7.8-7 9.5-4-1.7-7-5-7-9.5V5.5z" />
    <path d="M9 11.5l2 2 4-4.5" />
  </Icon>
);
export const IconBalance = (p: IconProps) => (
  <Icon {...p}>
    <path d="M4 19h16" />
    <path d="M7 19V9.5" /><path d="M12 19V5" /><path d="M17 19v-6.5" />
    <path d="M7 9.5l-2.2-2.2M7 9.5l2.2-2.2M17 12.5l-2.2-2.2M17 12.5l2.2-2.2" />
  </Icon>
);
export const IconApprove = (p: IconProps) => (
  <Icon {...p}>
    <path d="M4 5.5h16" />
    <path d="M4 12h10" />
    <path d="M4 18.5h7" />
    <path d="M14.5 18l1.8 1.8 3.7-4" />
  </Icon>
);
export const IconAudit = (p: IconProps) => (
  <Icon {...p}>
    <path d="M7 3h7l4 4v14H7z" />
    <path d="M14 3v4h4" />
    <path d="M9.5 11.5h6M9.5 14.5h6M9.5 17.5h3.5" />
  </Icon>
);

// Connection icons
export const IconEnvelope = (p: IconProps) => (
  <Icon {...p}>
    <rect x="3" y="5.5" width="18" height="13" rx="2.2" />
    <path d="M4 7.5l8 5.5 8-5.5" />
  </Icon>
);
export const IconSync = (p: IconProps) => (
  <Icon {...p}>
    <path d="M4 8a8 8 0 0 1 13.5-3.5L20 7" />
    <path d="M20 4v3h-3" />
    <path d="M20 16a8 8 0 0 1-13.5 3.5L4 17" />
    <path d="M4 20v-3h3" />
  </Icon>
);
export const IconCheck = ({ size = 16, style }: IconProps) => (
  <svg width={size} height={size} viewBox="0 0 24 24" style={style} aria-hidden="true">
    <path d="M5 12.5l4.2 4.2L19 7" {...stroke} strokeWidth={2.2} />
  </svg>
);
export const IconArrow = ({ size = 18, style }: IconProps) => (
  <svg width={size} height={size} viewBox="0 0 24 24" style={style} aria-hidden="true">
    <path d="M4 12h15M13 6l6 6-6 6" {...stroke} strokeWidth={1.8} />
  </svg>
);
export const IconClose = ({ size = 18, style }: IconProps) => (
  <svg width={size} height={size} viewBox="0 0 24 24" style={style} aria-hidden="true">
    <path d="M6 6l12 12M18 6L6 18" {...stroke} strokeWidth={1.8} />
  </svg>
);

// ---- Logo: open "C" of three rounded bars + green/red dots ----
// mode: 'static' | 'pulse' (gentle wave) | 'assemble' (draw-in + dot pop)
export type LogoMode = "static" | "pulse" | "assemble";

export function Logo({ scale = 1, mode = "static" }: { scale?: number; mode?: LogoMode }) {
  const barH = 5 * scale;
  const barColor = "#1D1D1F";
  const bars = [
    { w: 30 * scale, top: 0 },
    { w: 22 * scale, top: 8 * scale },
    { w: 30 * scale, top: 16 * scale },
  ];
  const h = 16 * scale + barH;
  return (
    <div className={"logo logo-" + mode} style={{ position: "relative", width: 30 * scale + 8 * scale, height: h }}>
      {bars.map((b, i) => (
        <div
          key={i}
          className="logo-bar"
          style={{
            position: "absolute",
            left: 0,
            top: b.top,
            width: b.w,
            height: barH,
            borderRadius: barH,
            background: barColor,
            animationDelay: i * 0.15 + "s",
          }}
        />
      ))}
      <div className="logo-dot logo-dot-green" style={{
        position: "absolute", left: bars[0].w + 3 * scale, top: 0,
        width: barH, height: barH, borderRadius: "50%", background: "#34C759",
      }} />
      <div className="logo-dot logo-dot-red" style={{
        position: "absolute", left: bars[2].w + 3 * scale, top: 16 * scale,
        width: barH, height: barH, borderRadius: "50%", background: "#FF3B30",
      }} />
    </div>
  );
}

export function Wordmark({ size = 19 }: { size?: number }) {
  return (
    <span style={{ fontSize: size, fontWeight: 700, letterSpacing: "-0.02em", color: "#1D1D1F" }}>
      Contrack AI
    </span>
  );
}

// ============================================================
// CONTENT
// Static structure only (id / icon / accent). All copy — titles,
// descriptions, building-screen lines — lives in ./i18n.ts so the
// flow can render in Norwegian or English. Keyed by `id`.
// ============================================================
export interface IndustryDef { id: string; icon: (p: IconProps) => ReactElement; }
export interface GoalDef { id: string; icon: (p: IconProps) => ReactElement; accent: string; }

export const INDUSTRIES: IndustryDef[] = [
  { id: "bygg", icon: IconConstruction },
  { id: "eiendom", icon: IconRealEstate },
  { id: "restaurant", icon: IconRestaurant },
  { id: "logistikk", icon: IconLogistics },
  { id: "industri", icon: IconIndustry },
  { id: "annet", icon: IconOther },
];

export const GOALS: GoalDef[] = [
  { id: "feil", icon: IconShield, accent: "#FF3B30" },
  { id: "saldo", icon: IconBalance, accent: "#34C759" },
  { id: "attestering", icon: IconApprove, accent: "#007AFF" },
  { id: "revisjon", icon: IconAudit, accent: "#5856D6" },
];

// Volume / project-count ranges are numeric and identical in both languages.
export const INVOICE_VOLUMES = ["Under 50", "50–200", "200–1000", "Over 1000"];
export const PROJECT_COUNTS = ["1–5", "6–20", "21–50", "Over 50"];

// ── Land + kontraktstype (onboarding steg 1-2) ──────────────────────────────
// contract_type (lagret verdi) styrer valideringsgren i matcher.py.
// Ekte flagg som skarpe inline-SVG (ikke emoji — ingen Apple-glyfer, ingen npm-avhengighet).
// Regioner uten eget nasjonalflagg (Midtøsten, Internasjonal) får en diskret globe.
// Rendres i en avrundet, kantet beholder i StepCountry.
const nordicCross = (bg: string, cross: string): ReactElement => (
  <svg viewBox="0 0 30 20" width="100%" height="100%" preserveAspectRatio="none">
    <rect width="30" height="20" fill={bg} />
    <rect x="9.5" width="3.4" height="20" fill={cross} />
    <rect y="8.3" width="30" height="3.4" fill={cross} />
  </svg>
);
const globeFlag: ReactElement = (
  <svg viewBox="0 0 30 20" width="100%" height="100%" preserveAspectRatio="none">
    <rect width="30" height="20" fill="#F4F4F5" />
    <g stroke="#71717A" strokeWidth="1" fill="none">
      <circle cx="15" cy="10" r="6.4" />
      <ellipse cx="15" cy="10" rx="2.6" ry="6.4" />
      <line x1="8.6" y1="10" x2="21.4" y2="10" />
    </g>
  </svg>
);

export const COUNTRY_FLAGS: Record<string, ReactElement> = {
  no: (
    <svg viewBox="0 0 30 20" width="100%" height="100%" preserveAspectRatio="none">
      <rect width="30" height="20" fill="#EF2B2D" />
      <rect x="9" width="5" height="20" fill="#fff" />
      <rect y="7.5" width="30" height="5" fill="#fff" />
      <rect x="10.5" width="2" height="20" fill="#002868" />
      <rect y="9" width="30" height="2" fill="#002868" />
    </svg>
  ),
  se: nordicCross("#006AA7", "#FECC00"),
  dk: nordicCross("#C8102E", "#ffffff"),
  fi: nordicCross("#ffffff", "#003580"),
  pl: (
    <svg viewBox="0 0 30 20" width="100%" height="100%" preserveAspectRatio="none">
      <rect width="30" height="20" fill="#ffffff" />
      <rect y="10" width="30" height="10" fill="#DC143C" />
    </svg>
  ),
  de: (
    <svg viewBox="0 0 30 20" width="100%" height="100%" preserveAspectRatio="none">
      <rect width="30" height="20" fill="#000000" />
      <rect y="6.67" width="30" height="6.67" fill="#DD0000" />
      <rect y="13.33" width="30" height="6.67" fill="#FFCE00" />
    </svg>
  ),
  fr: (
    <svg viewBox="0 0 30 20" width="100%" height="100%" preserveAspectRatio="none">
      <rect width="30" height="20" fill="#ffffff" />
      <rect width="10" height="20" fill="#0055A4" />
      <rect x="20" width="10" height="20" fill="#EF4135" />
    </svg>
  ),
  me: globeFlag,
  intl: globeFlag,
};

export interface CountryDef { id: string; }
export const COUNTRIES: CountryDef[] = [
  { id: "no" }, { id: "me" }, { id: "pl" }, { id: "intl" }, { id: "se" },
  { id: "dk" }, { id: "fi" }, { id: "de" }, { id: "fr" },
];

// Landets kontraktstyper (lagret verdi → matcher-gren). Filtrerer steg 2.
export const COUNTRY_CONTRACT_TYPES: Record<string, string[]> = {
  no: ["NS8405", "NS8407"],
  me: ["FIDIC"],
  pl: ["FIDIC"],
  intl: ["FIDIC"],
  se: ["AB04", "ABT06"],
  dk: ["AB18", "ABT18"],
  fi: ["YSE1998"],
  de: ["VOBB"],
  fr: ["NFP03001"],
};

export interface OnboardingAnswers {
  country: string | null;
  contractType: string | null;
  industry: string | null;
  goals: string[];
  projects: string | null;
  invoices: string | null;
  email: boolean;
  erp: boolean;
}

// Building-screen lines, dynamic by niche + goals, in the active language.
export function buildingLines({ industry, goals, projects, email, erp }: OnboardingAnswers, copy: ObCopy): string[] {
  const b = copy.building;
  const lines: string[] = [];
  lines.push(b.validation[industry || "annet"] || b.validation.annet);

  if (goals.includes("feil")) lines.push(b.lineByLine);
  if (goals.includes("saldo")) lines.push(b.balance);
  if (goals.includes("attestering")) lines.push(b.approval);
  if (goals.includes("revisjon")) lines.push(b.audit);
  if (goals.length === 0) lines.push(b.lineByLine);

  if (projects) lines.push(b.projects(projects));

  if (email && erp) lines.push(b.connectBoth);
  else if (email) lines.push(b.connectEmail);
  else if (erp) lines.push(b.connectErp);

  lines.push(b.workspace);
  return lines;
}
